import { isNil } from 'lodash';

import {
  ACTION_STATE_APPEARANCE,
  REQUEST_STATE_APPEARANCE,
} from '~/components/ClaimPage/CliveApp/ConductorHub/constants';
import {
  type ConductorStatusObservation,
  isKnownStatus,
} from '~/components/ClaimPage/CliveApp/conductorStatusObservation';
import { ClaimConductorStatusResponseConductorStatus as ConductorStatus } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import { TEXTS } from './texts';

/** What Clive's status asks of the handler, which the pill's dot shows. */
export type StatusTone = 'working' | 'waiting' | 'yourMove' | 'failed' | 'idle';

/**
 * The dot of each tone, in the Conductor Hub's palette, so the pill and the hub's log say the same thing in the
 * same colour: green for Clive at work, purple for a request waiting on the handler, orange for a failed action.
 * Idle is lighter than the handler's dot, so the two do not read alike.
 */
export const TONE_DOT_CLASS: Record<StatusTone, string> = {
  working: cn(ACTION_STATE_APPEARANCE.executed.node, 'animate-pulse'),
  waiting: ACTION_STATE_APPEARANCE.executed.node,
  yourMove: REQUEST_STATE_APPEARANCE.pending.node,
  failed: ACTION_STATE_APPEARANCE.failed.node,
  idle: 'bg-slate-400',
};

export interface StatusAppearance {
  tone: StatusTone;
  text: string;
  tooltip: string;
}

/** Keyed on the generated enum, so a status a new SDK brings fails `tsc` until it has a row here. */
const STATUS_APPEARANCE: Record<ConductorStatus, StatusAppearance> = {
  [ConductorStatus.Preparing]: { tone: 'working', ...TEXTS.STATUS.PREPARING },
  [ConductorStatus.Running]: { tone: 'working', ...TEXTS.STATUS.RUNNING },
  [ConductorStatus.WaitingOnExperts]: { tone: 'waiting', ...TEXTS.STATUS.WAITING_ON_EXPERTS },
  [ConductorStatus.Yielded]: { tone: 'waiting', ...TEXTS.STATUS.YIELDED },
  [ConductorStatus.PendingApproval]: { tone: 'yourMove', ...TEXTS.STATUS.PENDING_APPROVAL },
  [ConductorStatus.Escalated]: { tone: 'yourMove', ...TEXTS.STATUS.ESCALATED },
  [ConductorStatus.Error]: { tone: 'failed', ...TEXTS.STATUS.ERROR },
  [ConductorStatus.Interrupted]: { tone: 'failed', ...TEXTS.STATUS.INTERRUPTED },
  [ConductorStatus.Crashed]: { tone: 'failed', ...TEXTS.STATUS.CRASHED },
  [ConductorStatus.Completed]: { tone: 'idle', ...TEXTS.STATUS.COMPLETED },
  [ConductorStatus.Stopped]: { tone: 'idle', ...TEXTS.STATUS.STOPPED },
  [ConductorStatus.Replied]: { tone: 'idle', ...TEXTS.STATUS.REPLIED },
};

const NEEDS_YOU: StatusAppearance = { tone: 'yourMove', ...TEXTS.STATUS.NEEDS_YOU };
const NOT_STARTED: StatusAppearance = { tone: 'idle', ...TEXTS.STATUS.NOT_STARTED };
const UNKNOWN_ACTIVE: StatusAppearance = { tone: 'working', ...TEXTS.STATUS.UNKNOWN_ACTIVE };
const UNKNOWN_IDLE: StatusAppearance = { tone: 'idle', ...TEXTS.STATUS.UNKNOWN_IDLE };

/**
 * What the pill shows for a status. claims_llm can ship a status before CMS knows it, so one CMS does not know
 * reads by whether Clive is active rather than leaving the pill blank.
 */
export const getStatusAppearance = ({
  conductorStatus,
  isActive,
  openAsks = 0,
}: ConductorStatusObservation): StatusAppearance => {
  if (isNil(conductorStatus)) return NOT_STARTED;
  // A finished run with asks still open waits on the handler's answer.
  if (conductorStatus === ConductorStatus.Completed && openAsks > 0) return NEEDS_YOU;
  if (isKnownStatus(conductorStatus)) return STATUS_APPEARANCE[conductorStatus];
  return isActive ? UNKNOWN_ACTIVE : UNKNOWN_IDLE;
};
